import { Loader, useTheme } from '@ytl/common-web';
import { useCallback, useEffect, useRef } from 'react';
import { useLanguage } from '../../../../contexts/languageContext';
import { OnboardingPhase, OnboardingStepperControls } from '../../../../modules/onboarding/OnboardingComponent';
import { dapResource } from '../../../resource/dap.resource';
import { gripResource } from '../../../resource/grip.resource';
import { saccResource } from '../../../resource/sacc.resource';
import { parseJWT } from '../../../utils/jwtUtils';

type GetCustomerBy4TTokenPayload = {
  customerData: any;
};

type KYCDataCheckerProps = {
  stepper: OnboardingStepperControls;
  setCustomerData: (data: any) => void;
};

export const KYCDataChecker = ({ stepper, setCustomerData }: KYCDataCheckerProps) => {
  const { translation } = useLanguage();
  const { isWebView } = useTheme();
  const hasFetchedRef = useRef(false);

  const fetchIdentityData = useCallback(async () => {
    if (hasFetchedRef.current) {
      return;
    }

    hasFetchedRef.current = true;

    try {
      // Extract externalId from URL query params
      const params = new URLSearchParams(window.location.search);
      const externalId = params.get('externalId');

      if (!externalId) {
        console.error('No externalId found in URL params');
        stepper.goToPhase(OnboardingPhase.KYCError);
        return;
      }

      // Call getIdentity to retrieve verification data JWT and decode payload
      const identityJwt = await gripResource.getIdentity(externalId);
      if (identityJwt) {
        const validateRequest = await saccResource.validateIdentity(identityJwt);
        if (validateRequest.isSuccess) {
          // Verification successful, proceed
          const customerJwt = await dapResource.getCustomerBy4T(identityJwt);
          const customerPayload = parseJWT<GetCustomerBy4TTokenPayload>(customerJwt);
          if (!customerPayload) {
            console.error('Invalid customer JWT payload');
            stepper.goToPhase(OnboardingPhase.KYCError);
            return;
          }

          setCustomerData(customerJwt);
          stepper.goToNext();
          return;
        } else {
          console.error('Identity validation failed:', validateRequest);
          stepper.goToPhase(OnboardingPhase.KYCError);
          return;
        }
      }

      console.error('No identity data returned');
      stepper.goToPhase(OnboardingPhase.KYCError);
      return;
    } catch (err) {
      console.error('Error fetching identity data:', err);
      stepper.goToPhase(OnboardingPhase.KYCError);
      return;
    }
  }, [setCustomerData, stepper]);

  useEffect(() => {
    void fetchIdentityData();
  }, [fetchIdentityData]);

  return (
    <div className={`onboarding-step${isWebView ? ' onboarding-step--webview' : ''}`}>
      <div className="onboarding-step__KYC-check-loader">
        <Loader isLoading />
        <p
          className="onboarding-step__KYC-check-loader-text"
          dangerouslySetInnerHTML={{ __html: translation.onboarding?.kyc?.checking }}
        ></p>
      </div>
    </div>
  );
};
